import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class TranslatedPaginatorIntl extends MatPaginatorIntl {
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateLabels());
    this.updateLabels();
  }

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    const safeLength = Math.max(length, 0);
    const firstItemIndex = page * pageSize;
    const hasItems = safeLength > 0 && pageSize > 0;
    const start = hasItems ? firstItemIndex + 1 : 0;
    let end = 0;
    if (hasItems) {
      end = firstItemIndex < safeLength ? Math.min(firstItemIndex + pageSize, safeLength) : firstItemIndex + pageSize;
    }

    return this.translate.instant('paginator-range-label', {
      start,
      end,
      total: safeLength,
    });
  };

  private updateLabels(): void {
    this.itemsPerPageLabel = this.translate.instant('paginator-items-per-page');
    this.nextPageLabel = this.translate.instant('paginator-next-page');
    this.previousPageLabel = this.translate.instant('paginator-previous-page');
    this.firstPageLabel = this.translate.instant('paginator-first-page');
    this.lastPageLabel = this.translate.instant('paginator-last-page');
    this.changes.next();
  }
}

@Injectable()
export class TranslatedDatepickerIntl extends MatDatepickerIntl {
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateLabels());
    this.updateLabels();
  }

  override formatYearRangeLabel(start: string, end: string): string {
    return this.translate.instant('datepicker-year-range-label', { start, end });
  }

  private updateLabels(): void {
    this.calendarLabel = this.translate.instant('datepicker-calendar');
    this.openCalendarLabel = this.translate.instant('datepicker-open-calendar');
    this.closeCalendarLabel = this.translate.instant('datepicker-close-calendar');
    this.prevMonthLabel = this.translate.instant('datepicker-previous-month');
    this.nextMonthLabel = this.translate.instant('datepicker-next-month');
    this.prevYearLabel = this.translate.instant('datepicker-previous-year');
    this.nextYearLabel = this.translate.instant('datepicker-next-year');
    this.prevMultiYearLabel = this.translate.instant('datepicker-previous-years');
    this.nextMultiYearLabel = this.translate.instant('datepicker-next-years');
    this.switchToMonthViewLabel = this.translate.instant('datepicker-choose-date');
    this.switchToMultiYearViewLabel = this.translate.instant('datepicker-choose-month-year');
    this.startDateLabel = this.translate.instant('datepicker-start-date');
    this.endDateLabel = this.translate.instant('datepicker-end-date');
    this.comparisonDateLabel = this.translate.instant('datepicker-comparison-range');
    this.changes.next();
  }
}
